/**
 * Entry List detail page (pages/entry-list?competition=<handle>).
 *
 * Hydrates the `entry-list-detail` section shell:
 *   1. /products/<handle>.js                      → title, image, numeric id
 *   2. /apps/competitions/competition-status      → status/dates/sold/total
 *   3. /apps/competitions/entries                 → paginated + searchable rows
 *      (q matches an exact ticket number or a case-insensitive name), plus
 *      `has_instant_wins` and a per-row `instant_win` {prize_title, image_url}
 *      driving the optional Instant Win column
 *
 * ?product_id=<id> is accepted as a legacy alias: the entries endpoint works
 * directly off it (hero product lookup is skipped unless a handle is present).
 * No param at all → back to the index.
 *
 * All Tailwind classes used on screen live in the section markup (or are added
 * from a small fixed set below that also appears in section comments), so the
 * CSS build — which scans liquid — emits everything needed.
 */
(function () {
  "use strict";

  var root = document.querySelector("[data-cuk-entry-detail]");
  if (!root) return;

  var params = new URLSearchParams(window.location.search);
  var handle = params.get("competition") || params.get("product") || params.get("comp");
  var pidParam = params.get("product_id");
  if (!handle && !pidParam) {
    window.location.replace("/pages/entry-lists");
    return;
  }

  var endpoint = root.getAttribute("data-endpoint") || "/apps/competitions/entries";
  var statusEndpoint =
    root.getAttribute("data-status-endpoint") || "/apps/competitions/competition-status";
  var defaultPageSize = parseInt(root.getAttribute("data-page-size") || "25", 10) || 25;

  // --- element handles -----------------------------------------------------
  var crumb = root.querySelector("[data-cuk-crumb]");
  var hero = root.querySelector("[data-cuk-hero]");
  var heroImageLink = root.querySelector("[data-cuk-hero-image-link]");
  var heroImage = root.querySelector("[data-cuk-hero-image]");
  var titleEl = root.querySelector("[data-cuk-title]");
  var statusBadge = root.querySelector("[data-cuk-status-badge]");
  var drawLine = root.querySelector("[data-cuk-drawline]");
  var drawDateEl = root.querySelector("[data-cuk-drawdate]");
  var countdownChip = root.querySelector("[data-cuk-countdown]");
  var cdTime = root.querySelector("[data-cuk-cd-time]");
  var progressWrap = root.querySelector("[data-cuk-progress-wrap]");
  var progressBar = root.querySelector("[data-cuk-progress-bar]");
  var soldPct = root.querySelector("[data-cuk-sold-pct]");
  var remainingEl = root.querySelector("[data-cuk-remaining]");
  var productLink = root.querySelector("[data-cuk-product-link]");
  var winnerWrap = root.querySelector("[data-cuk-winner]");
  var winnerTicketEl = root.querySelector("[data-cuk-winner-ticket]");
  var winnerNameEl = root.querySelector("[data-cuk-winner-name]");
  var winnerLink = root.querySelector("[data-cuk-winner-link]");
  var panel = root.querySelector("[data-cuk-panel]");
  var countEl = root.querySelector("[data-cuk-count]");
  var searchInput = root.querySelector("[data-cuk-search]");
  var pageSizeSelect = root.querySelector("[data-cuk-pagesize]");
  var spinner = root.querySelector("[data-cuk-spinner]");
  var tableWrap = root.querySelector("[data-cuk-table-wrap]");
  var tbody = root.querySelector("[data-cuk-tbody]");
  var emptyEl = root.querySelector("[data-cuk-empty]");
  var pagination = root.querySelector("[data-cuk-pagination]");
  var prevBtn = root.querySelector("[data-cuk-prev]");
  var nextBtn = root.querySelector("[data-cuk-next]");
  var pagesWrap = root.querySelector("[data-cuk-pages]");
  var prizeTh = root.querySelector('[data-cuk-th="prize"]');
  var rowTemplate = root.querySelector("template[data-cuk-row]");
  var pageBtnTemplate = root.querySelector("template[data-cuk-page-btn]");

  var state = {
    productId: pidParam || null,
    handle: handle,
    page: 1,
    pageSize: defaultPageSize,
    q: "",
    total: 0,
    pageCount: 1,
    winnerTicket: null,
    hasInstantWins: false,
    reqId: 0,
  };
  var cdTimer = null;
  var searchTimer = null;

  var STATUS_LABELS = {
    scheduled: "Coming Soon",
    active: "Open",
    live: "Live",
    pending_draw: "Awaiting Draw",
    ended: "Winner Drawn",
    cancelled: "Cancelled",
    closed: "Awaiting Draw",
    sold_out: "Sold Out",
    drawn: "Winner Drawn",
  };

  function pad(n) {
    return String(n).padStart(2, "0");
  }

  if (pageSizeSelect) {
    var hasDefault = Array.prototype.some.call(pageSizeSelect.options, function (o) {
      return parseInt(o.value, 10) === defaultPageSize;
    });
    if (!hasDefault) {
      var opt = document.createElement("option");
      opt.value = String(defaultPageSize);
      opt.textContent = String(defaultPageSize);
      pageSizeSelect.insertBefore(opt, pageSizeSelect.firstChild);
    }
    pageSizeSelect.value = String(defaultPageSize);
  }

  // --- hero ---------------------------------------------------------------
  function hydrateProduct(product) {
    var title = product.title || "Entry List";
    if (crumb) crumb.textContent = title;
    if (titleEl) titleEl.textContent = title;
    document.title = title + " — Entry List";
    var url = "/products/" + (product.handle || state.handle);
    if (productLink) {
      productLink.href = url;
      productLink.hidden = false;
    }
    if (heroImageLink) heroImageLink.href = url;
    var img = product.featured_image || (product.images && product.images[0]) || null;
    if (img && heroImage) {
      heroImage.src = img;
      heroImage.alt = title;
      heroImage.hidden = false;
    }
  }

  function startCountdown(drawIso, status) {
    if (!countdownChip || !cdTime || !drawIso) return;
    var drawTs = Math.floor(new Date(drawIso).getTime() / 1000);
    if (!isFinite(drawTs)) return;
    var running = status === "active" || status === "live";

    function tick() {
      var now = Math.floor(Date.now() / 1000);
      var s = drawTs - now;
      if (s <= 0 || !running) {
        countdownChip.hidden = true;
        if (cdTimer) {
          clearInterval(cdTimer);
          cdTimer = null;
        }
        return;
      }
      var d = Math.floor(s / 86400);
      var h = Math.floor((s % 86400) / 3600);
      var m = Math.floor((s % 3600) / 60);
      var sec = s % 60;
      var text = (d > 0 ? d + "d " : "") + pad(h) + ":" + pad(m) + ":" + pad(sec);
      if (window.cukTickSwap) window.cukTickSwap(cdTime, text);
      else cdTime.textContent = text;
      countdownChip.hidden = false;
    }

    tick();
    if (running) cdTimer = setInterval(tick, 1000);
  }

  function hydrateStatus(data) {
    if (!data || data.found === false) return;
    var status = data.status || "active";
    if (statusBadge) {
      statusBadge.textContent = STATUS_LABELS[status] || status;
      statusBadge.hidden = false;
    }
    if (data.draw_date && drawLine && drawDateEl) {
      var d = new Date(data.draw_date);
      if (!isNaN(d.getTime())) {
        drawDateEl.textContent =
          (status === "ended" || status === "drawn" ? "Drawn " : "Draw ") +
          d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
        drawLine.hidden = false;
      }
    }
    if (typeof data.percent_sold === "number" && progressWrap) {
      if (progressBar) progressBar.style.width = Math.min(100, data.percent_sold) + "%";
      if (soldPct) soldPct.textContent = data.percent_sold + "% sold";
      if (remainingEl && typeof data.remaining === "number") {
        remainingEl.textContent = data.remaining.toLocaleString("en-GB") + " left";
      }
      progressWrap.hidden = false;
    }
    startCountdown(data.draw_date, status);
  }

  // --- entries ------------------------------------------------------------
  function setLoading(loading) {
    if (spinner) spinner.hidden = !loading;
  }

  /**
   * Fills one row's instant-win cell. A fresh clone already defaults to hidden
   * cell + hidden pill + visible em-dash, so only the positive cases need work.
   */
  function renderPrizeCell(node, win) {
    var cell = node.querySelector('[data-cuk-cell="prize"]');
    if (!cell) return;
    cell.hidden = !state.hasInstantWins;
    if (!state.hasInstantWins || !win || !win.prize_title) return;

    var pill = cell.querySelector("[data-cuk-prize]");
    var title = cell.querySelector("[data-cuk-prize-title]");
    var img = cell.querySelector("[data-cuk-prize-img]");
    var none = cell.querySelector("[data-cuk-prize-none]");
    if (title) title.textContent = win.prize_title;
    if (img && win.image_url) {
      img.src = win.image_url;
      img.alt = "";
      img.hidden = false;
    }
    if (pill) pill.hidden = false;
    if (none) none.hidden = true;
  }

  function renderRows(entries) {
    if (!tbody || !rowTemplate) return;
    tbody.innerHTML = "";
    entries.forEach(function (entry, i) {
      var node = rowTemplate.content.cloneNode(true);
      var row = node.querySelector("[data-cuk-row-el]");
      node.querySelector('[data-cuk-cell="index"]').textContent = entry.index;
      node.querySelector('[data-cuk-cell="ticket"]').textContent = entry.ticket_number;
      node.querySelector('[data-cuk-cell="name"]').textContent = entry.name;
      renderPrizeCell(node, entry.instant_win);
      if (row) {
        row.style.setProperty("--cuk-row-delay", Math.min(i * 22, 440) + "ms");
        if (
          state.winnerTicket != null &&
          String(entry.ticket_number) === String(state.winnerTicket)
        ) {
          // Winner row highlight (classes also present in the section markup).
          row.classList.add("bg-prize-tint", "ring-1", "ring-prize/40");
        }
      }
      tbody.appendChild(node);
    });
  }

  function renderPagination() {
    if (!pagination) return;
    pagination.hidden = state.pageCount <= 1;
    if (prevBtn) prevBtn.disabled = state.page <= 1;
    if (nextBtn) nextBtn.disabled = state.page >= state.pageCount;
    if (!pagesWrap || !pageBtnTemplate) return;
    pagesWrap.innerHTML = "";

    // Windowed page numbers: 1 … (p−1) p (p+1) … last
    var pages = [];
    var last = state.pageCount;
    for (var p = 1; p <= last; p++) {
      if (p === 1 || p === last || Math.abs(p - state.page) <= 1) pages.push(p);
      else if (pages[pages.length - 1] !== "…") pages.push("…");
    }
    pages.forEach(function (p) {
      if (p === "…") {
        var dots = document.createElement("span");
        dots.className = "px-1.5 text-ink-muted";
        dots.textContent = "…";
        pagesWrap.appendChild(dots);
        return;
      }
      var node = pageBtnTemplate.content.cloneNode(true);
      var btn = node.querySelector("button");
      btn.textContent = p;
      if (p === state.page) {
        btn.setAttribute("data-active", "");
        btn.setAttribute("aria-current", "page");
      }
      btn.addEventListener("click", function () {
        if (p !== state.page) goToPage(p);
      });
      pagesWrap.appendChild(node);
    });
  }

  function renderCount() {
    if (!countEl) return;
    if (state.q) {
      countEl.textContent =
        state.total.toLocaleString("en-GB") +
        (state.total === 1 ? " match" : " matches") +
        " for “" + state.q + "”";
    } else {
      countEl.textContent =
        state.total.toLocaleString("en-GB") + (state.total === 1 ? " entry" : " entries");
    }
  }

  function load() {
    if (!state.productId) return;
    var reqId = ++state.reqId;
    setLoading(true);
    var url =
      endpoint +
      "?product_id=" + encodeURIComponent(state.productId) +
      "&page=" + state.page +
      "&page_size=" + state.pageSize +
      (state.q ? "&q=" + encodeURIComponent(state.q) : "");
    fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (res) {
        if (!res.ok) throw new Error("entries " + res.status);
        return res.json();
      })
      .then(function (data) {
        if (reqId !== state.reqId) return; // stale response
        state.total = data.total || 0;
        state.pageCount = data.page_count || 1;
        state.page = data.page || 1;
        // Per-competition, so the column survives paging onto a page with no wins.
        state.hasInstantWins = !!data.has_instant_wins;
        if (prizeTh) prizeTh.hidden = !state.hasInstantWins;
        if (data.winner && data.winner.ticket_number != null) {
          state.winnerTicket = data.winner.ticket_number;
          if (winnerWrap && winnerTicketEl) {
            winnerTicketEl.textContent = "#" + data.winner.ticket_number;
            if (winnerNameEl) winnerNameEl.textContent = data.winner.name || "Entrant";
            if (winnerLink && state.handle) {
              winnerLink.href = "/blogs/winners/winner-" + state.handle;
            }
            winnerWrap.hidden = false;
          }
        }
        renderCount();
        var entries = data.entries || [];
        if (entries.length === 0) {
          if (tableWrap) tableWrap.hidden = true;
          if (emptyEl) {
            emptyEl.textContent = state.q
              ? "No entries match your search — try a different name or ticket number."
              : "No entries yet — be the first to enter this competition!";
            emptyEl.hidden = false;
          }
        } else {
          if (emptyEl) emptyEl.hidden = true;
          if (tableWrap) tableWrap.hidden = false;
          renderRows(entries);
        }
        renderPagination();
      })
      .catch(function () {
        if (reqId !== state.reqId) return;
        if (tableWrap) tableWrap.hidden = true;
        if (emptyEl) {
          emptyEl.textContent = "Could not load the entry list — please try again shortly.";
          emptyEl.hidden = false;
        }
      })
      .finally(function () {
        if (reqId === state.reqId) setLoading(false);
      });
  }

  function goToPage(page) {
    state.page = Math.max(1, Math.min(page, state.pageCount));
    load();
    if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // --- events ----------------------------------------------------------------
  if (searchInput) {
    searchInput.addEventListener("input", function () {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(function () {
        state.q = searchInput.value.trim();
        state.page = 1;
        load();
      }, 300);
    });
  }
  if (pageSizeSelect) {
    pageSizeSelect.addEventListener("change", function () {
      state.pageSize = parseInt(pageSizeSelect.value, 10) || defaultPageSize;
      state.page = 1;
      load();
    });
  }
  if (prevBtn) prevBtn.addEventListener("click", function () { goToPage(state.page - 1); });
  if (nextBtn) nextBtn.addEventListener("click", function () { goToPage(state.page + 1); });

  // --- boot ------------------------------------------------------------------
  function reveal() {
    if (hero) hero.hidden = false;
    if (panel) panel.hidden = false;
  }

  function bootWithProductId() {
    reveal();
    fetch(statusEndpoint + "?product_id=" + encodeURIComponent(state.productId), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) { if (data) hydrateStatus(data); })
      .catch(function () {});
    load();
  }

  if (state.handle) {
    fetch("/products/" + encodeURIComponent(state.handle) + ".js", {
      headers: { Accept: "application/json" },
    })
      .then(function (res) {
        if (!res.ok) throw new Error("product " + res.status);
        return res.json();
      })
      .then(function (product) {
        state.productId = String(product.id);
        hydrateProduct(product);
        bootWithProductId();
      })
      .catch(function () {
        // Unknown handle — nothing to show here.
        window.location.replace("/pages/entry-lists");
      });
  } else {
    if (crumb) crumb.textContent = "Entry List";
    if (titleEl) titleEl.textContent = "Entry List";
    bootWithProductId();
  }
})();
